/**
 *
 *  About TruBe page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 18/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import AskConciergeModal from "../Homepage/AskConciergeModal/AskConciergeModal";
import AppBanner from "../AppBanner/AppBanner";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth } from "../../utils";

// Icons
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Images
import aboutHero from "../../images/about-hero.jpg";
import aboutHero2x from "../../images/about-hero@2x.jpg";
import aboutHeroMobile from "../../images/about-hero-mobile.jpg";
import aboutHeroMobile2x from "../../images/about-hero-mobile@2x.jpg";
import aboutBeBetter from "../../images/about-be-better.jpg";
import aboutBeBetter2x from "../../images/about-be-better@2x.jpg";
import TruBeLogoVertical from "./TruBeLogoVertical";
import DoubleBedIcon from "./DoubleBedIcon";
import HealthyFoodIcon from "./HealthyFoodIcon";
import HeartbeatIcon from "./HeartbeatIcon";
import LotusIcon from "./LotusIcon";
import SneakersIcon from "./SneakersIcon";
import SpaIcon from "./SpaIcon";

import fitnessPillars from "./pillars-fitness.jpg";
import fitnessPillars2x from "./pillars-fitness@2x.jpg";
import passionPillars from "./pillars-passion.jpg";
import passionPillars2x from "./pillars-passion@2x.jpg";
import spiritPillars from "./pillars-spirit.jpg";
import spiritPillars2x from "./pillars-spirit@2x.jpg";
import bodyPillars from "./pillars-body.jpg";
import bodyPillars2x from "./pillars-body@2x.jpg";

import BeBetterThanYouWereYesterday from "./BeBetterThanYouWereYesterday";
import QuoteMark from "../Testimonials/QuoteMark";
import accessImg from "../Homepage/access.jpg";
import access2xImg from "../Homepage/access@2x.jpg";
import access3xImg from "../Homepage/access@3x.jpg";
import HowItWorks1 from "../Homepage/HowItWorks1";
import connectImg from "../Homepage/connect.jpg";
import connect2xImg from "../Homepage/connect@2x.jpg";
import connect3xImg from "../Homepage/connect@3x.jpg";
import HowItWorks2 from "../Homepage/HowItWorks2";
import trainImg from "../Homepage/train.jpg";
import train2xImg from "../Homepage/train@2x.jpg";
import train3xImg from "../Homepage/train@3x.jpg";
import HowItWorks3 from "../Homepage/HowItWorks3";
import progressImg from "../Homepage/progress.jpg";
import progress2xImg from "../Homepage/progress@2x.jpg";
import progress3xImg from "../Homepage/progress@3x.jpg";
import HowItWorks4 from "../Homepage/HowItWorks4";
import askConcierge from "../Homepage/AskConciergeModal/ask-concierge.svg";
import CaretDownIcon from "../../icons/CaretDownIcon";
import CaretUpIcon from "../../icons/CaretUpIcon";

const storiesSliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      centerMode: true,
      centerPadding: "48px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 2,
      centerMode: true,
      centerPadding: "50px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 1180,
    settings: {
      slidesToShow: 2,
      centerMode: true,
      centerPadding: "100px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "48px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "144px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1920,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "144px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 5,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "144px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 7,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "144px",
      initialSlide: 2,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

const SliderPrevArrow = ({ className, style, onClick }) => (
  <button className={className} style={style} onClick={onClick}>
    <ArrowLeftIcon className="tbk-inline" />
  </button>
);

const SliderNextArrow = ({ className, style, onClick }) => (
  <button className={className} style={style} onClick={onClick}>
    <ArrowRightIcon className="tbk-inline" />
  </button>
);

const SliderPrevArrowBig = ({ className, style, onClick }) => (
  <button
    className={`${className ? `${className} ` : ""}tbk-w-9 tbk-h-9`}
    style={style}
    onClick={onClick}
  >
    <ArrowLeftIcon className="tbk-inline" />
  </button>
);

const SliderNextArrowBig = ({ className, style, onClick }) => (
  <button
    className={`${className ? `${className} ` : ""}tbk-w-9 tbk-h-9`}
    style={style}
    onClick={onClick}
  >
    <ArrowRightIcon className="tbk-inline" />
  </button>
);

const TruBeInPressSlider = () => {
  var settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    useTransform: false,
    nextArrow: (
      <SliderNextArrow className="tbk-absolute tbk-top-1/2 tbk--left-2 tbk-rounded-full tbk-shadow-trube" />
    ),
    prevArrow: (
      <SliderPrevArrow className="tbk-absolute tbk-top-1/2 tbk--left-2 tbk-rounded-full tbk-shadow-trube" />
    ),
  };

  return (
    <Slider
      {...settings}
      className="tbk-relative tbk-touch-pan-y tbk-select-none lg:tbk-h-34"
    >
      <div>
        <div className="tbk-flex tbk-flex-col tbk-justify-evenly lg:tbk-grid lg:tbk-grid-cols-3 lg:tbk-gap-8">
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://techcrunch.com/2015/04/14/trube/?guccounter=1&amp;guce_referrer=aHR0cHM6Ly93d3cuZ29vZ2xlLmNvbS8&amp;guce_referrer_sig=AQAAANzWBmThbVS793oXi5rOjEo0tIC5bWsjuWwWvmOHr1c3RA7SSHAk66B_24IYHmdAWH15OYCpKG0bKNBvkCI8JWziTUzsGprFlMYUHZ56T5E4QausU4TzCn92hOGNVGpgS1mNM7a2jbkexO6BsMgcjRpYbUefZuiQnMbwnCLJZ_PP"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <svg
                width="224"
                height="32"
                viewBox="0 0 224 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="tbk-mb-2"
              >
                <path
                  d="M41.8083 10.6667H62.7314V0H41.8083V10.6667Z"
                  fill="#0A9E01"
                />
                <path
                  d="M0 0V10.6667H10.4429V32H20.8858V10.6667H31.366V0H0Z"
                  fill="#0A9E01"
                />
                <path
                  d="M31.3657 31.9998H62.7317V21.3332H41.8086V10.6665H31.3657V31.9998Z"
                  fill="#0A9E01"
                />
                <path
                  d="M89.3992 8.05713H74.0332V12.4954H79.1801V26.4068H84.2523V12.4954H89.3992V8.05713Z"
                  fill="#0A9E01"
                />
                <path
                  d="M95.1795 12.3462C91.5618 12.3462 88.8765 14.7704 88.8765 19.5443C88.8765 23.7588 91.1515 26.7052 95.2914 26.7052C97.4546 26.7052 99.2075 26.1457 100.886 25.1387L99.0956 22.1178C97.7156 22.8264 96.7459 23.0874 95.5152 23.0874C94.2471 23.0874 93.352 22.4161 93.1655 20.887H101.259C101.296 20.6632 101.296 20.3648 101.296 19.9546C101.333 14.845 98.9837 12.3462 95.1795 12.3462ZM93.2401 17.9406C93.3893 16.5606 93.986 15.852 95.0676 15.852C96.4476 15.852 96.9324 16.5606 97.1562 17.9406H93.2401Z"
                  fill="#0A9E01"
                />
                <path
                  d="M110.284 22.8637C109.016 22.8637 108.345 21.7448 108.345 19.5443C108.345 17.3812 108.941 16.1504 110.172 16.1504C111.179 16.1504 111.627 16.7098 112.298 17.9406L115.543 15.4791C114.349 13.5397 112.857 12.3462 110.247 12.3462C105.846 12.3462 103.683 15.2553 103.683 19.4697C103.683 24.0944 106.107 26.6679 110.135 26.6679C112.559 26.6679 114.051 25.7355 115.655 23.3858L112.671 21.0362C111.776 22.3788 111.179 22.8637 110.284 22.8637Z"
                  fill="#0A9E01"
                />
                <path
                  d="M126.546 12.345C124.905 12.345 123.935 12.9045 123.077 13.5385V7.31006L118.341 9.21216V26.4429H123.077V18.5735C123.077 16.8578 123.674 16.373 124.681 16.373C125.651 16.373 126.248 16.8578 126.248 18.6108V26.4429H130.984V18.014C130.984 14.3963 129.53 12.345 126.546 12.345Z"
                  fill="#0A9E01"
                />
                <path
                  d="M142.359 22.266C140.158 22.266 139.263 20.0282 139.263 17.231C139.263 14.3219 140.195 12.196 142.321 12.196C144 12.196 144.634 13.2776 145.268 14.9932L149.892 13.1657C148.624 9.69721 146.834 7.75781 142.321 7.75781C137.398 7.75781 133.93 11.3382 133.93 17.231C133.93 22.6762 136.839 26.7042 142.284 26.7042C146.648 26.7042 148.699 24.3545 149.93 21.9676L145.641 19.6925C144.634 21.4082 143.962 22.266 142.359 22.266Z"
                  fill="#111418"
                />
                <path
                  d="M157.538 13.6516V12.6446H152.802V26.4068H157.538V18.9103C157.538 17.232 158.247 16.6725 159.403 16.6725C160.336 16.6725 161.007 17.1574 161.604 17.8287L162.722 12.9429C162.163 12.6073 161.492 12.3462 160.485 12.3462C159.254 12.3462 158.322 12.8683 157.538 13.6516Z"
                  fill="#111418"
                />
                <path
                  d="M172.829 20.514C172.829 22.2296 172.233 22.7145 171.263 22.7145C170.293 22.7145 169.697 22.2296 169.697 20.4767V12.6445H164.96V21.0734C164.96 24.6912 166.415 26.7051 169.435 26.7051C171.039 26.7051 172.046 26.1457 172.867 25.5117V26.4441H177.603V12.6818H172.867V20.514"
                  fill="#111418"
                />
                <path
                  d="M189.203 12.3462C187.562 12.3462 186.592 12.9056 185.734 13.5397V12.6073H180.998V26.3695H185.734V18.5C185.734 16.7844 186.331 16.2996 187.338 16.2996C188.308 16.2996 188.904 16.7844 188.904 18.5373V26.3695H193.641V17.9406C193.641 14.3975 192.224 12.3462 189.203 12.3462Z"
                  fill="#111418"
                />
                <path
                  d="M203.039 22.8637C201.771 22.8637 201.1 21.7448 201.1 19.5443C201.1 17.3812 201.696 16.1504 202.89 16.1504C203.897 16.1504 204.344 16.7098 205.016 17.9406L208.261 15.4791C207.067 13.5397 205.575 12.3462 202.965 12.3462C198.564 12.3462 196.4 15.2553 196.4 19.4697C196.4 24.0944 198.825 26.6679 202.853 26.6679C205.277 26.6679 206.769 25.7355 208.372 23.3858L205.389 21.0362C204.568 22.3788 203.934 22.8637 203.039 22.8637Z"
                  fill="#111418"
                />
                <path
                  d="M219.301 12.345C217.66 12.345 216.69 12.9045 215.832 13.5385V7.31006L211.096 9.21216V26.4429H215.832V18.5735C215.832 16.8578 216.429 16.373 217.436 16.373C218.406 16.373 219.002 16.8578 219.002 18.6108V26.4429H223.739V18.014C223.776 14.3963 222.322 12.345 219.301 12.345Z"
                  fill="#111418"
                />
              </svg>
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “Booking the trainer is the easy part. An on-demand ass-kicking is
              a few taps away”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://thetimes.co.uk/article/the-best-apps-for-exercise-health-and-fitness-zqqkl3nmb"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <img
                className="tbk-inline-block"
                alt="The Times"
                width="278px"
                height="32px"
                src="https://static.trubeapp.com/staging/assets/images/update/press/times-black.png"
                srcSet="https://static.trubeapp.com/staging/assets/images/update/press/times-black@2x.png 2x"
              />
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “The best apps for exercise, health and fitness”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://womenshealthmag.com/uk/health/a702821/the-wellbeing-start-ups-on-our-radar/"
              target="_blank"
              className="tbk-inline-block"
              rel="noreferrer"
            >
              <svg
                width="157"
                height="32"
                viewBox="0 0 157 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="tbk-mb-2"
              >
                <path
                  d="M15.3001 31.7334L18.0583 3.74741C18.2144 2.31223 18.4746 2.07303 18.943 2.07303H19.2032V0.47839H14.7277V2.07303H15.0139C15.6384 2.07303 15.9506 2.52485 15.8726 3.74741L14.2853 20.4912L12.4639 3.74741C12.3077 2.44512 12.62 2.07303 13.3225 2.07303H13.4006V0.47839H7.15566V2.07303H7.46791C8.06638 2.07303 8.06638 2.31223 8.17047 3.74741L8.63884 8.02637L7.18168 21.5808L5.54239 3.77399C5.33422 2.33881 5.62045 2.09961 6.24494 2.09961H6.323V0.504969H0V2.09961H0.312246C0.962761 2.09961 1.0148 2.33881 1.17092 3.77399L3.98115 31.76H8.04036L9.62762 17.3816L11.2669 31.76C11.2669 31.7334 15.3001 31.7334 15.3001 31.7334ZM42.1794 13.395C42.1794 11.4549 42.1794 10.7638 43.3763 10.7638C44.209 10.7638 44.209 11.0828 44.209 12.704V29.3946C44.209 30.0059 44.0008 30.1122 43.6886 30.1122H43.5064V31.7068H49.8034V30.1122H49.5172C49.1009 30.1122 48.9708 29.9527 48.9708 29.3149V11.8269C48.9708 8.95658 48.3723 7.84033 45.6141 7.84033C44.0008 7.84033 42.8559 8.37188 42.1013 10.1526C41.7891 8.55792 40.9564 7.84033 38.8227 7.84033C37.0533 7.84033 36.1166 8.39846 35.4401 10.1526V7.84033L30.0538 8.29215V9.88679H30.288C30.6523 9.88679 30.9125 10.1526 30.9125 11.0562V29.2086C30.9125 29.9262 30.7563 30.1388 30.288 30.1388H30.0538V31.7334H36.0645V30.1388H35.8304C35.5181 30.1388 35.4401 30.0059 35.4401 29.5275V13.395C35.4401 11.4549 35.388 10.7638 36.611 10.7638C37.4436 10.7638 37.4436 11.0828 37.4436 12.704V29.3946C37.4436 30.0059 37.2615 30.1122 36.9753 30.1122H36.7151V31.7068H42.8819V30.1122H42.7779C42.3615 30.1122 42.1794 29.9527 42.1794 29.3149V13.395ZM86.1021 31.7334H93.6481V30.1388H93.1277C92.4772 30.1388 92.269 29.8464 92.269 28.4644V16.4248H96.2502V28.4644C96.2502 29.8464 96.068 30.1388 95.4175 30.1388H94.819V31.7334H102.313V30.1388H101.688C101.012 30.1388 100.908 29.8464 100.908 28.4644V3.74741C100.908 2.31223 101.012 2.07303 101.688 2.07303H102.313V0.47839H94.819V2.07303H95.4175C96.042 2.07303 96.2502 2.31223 96.2502 3.74741V14.5378H92.269V3.74741C92.269 2.31223 92.4512 2.07303 93.1277 2.07303H93.6481V0.47839H86.1021V2.07303H86.7787C87.4032 2.07303 87.6114 2.31223 87.6114 3.74741V29.2617C87.6114 29.9527 87.4292 30.1388 86.7787 30.1388H86.1021V31.7334ZM143.009 31.7334H148.968V30.1388H148.786C148.395 30.1388 148.343 30.0059 148.343 29.5275V13.395C148.343 11.4549 148.395 10.7638 149.592 10.7638C150.477 10.7638 150.477 11.0828 150.477 12.704V29.3946C150.477 30.0059 150.321 30.1122 150.061 30.1122H149.775V31.7068H156.098V30.1122H155.837C155.395 30.1122 155.317 29.9527 155.317 29.3149V11.8269C155.317 8.95658 154.614 7.84033 151.882 7.84033C150.087 7.84033 149.072 8.39846 148.343 10.1526V0L142.619 0.47839V2.07303H143.217C143.608 2.07303 143.842 2.31223 143.842 3.18929V29.2086C143.842 29.9262 143.712 30.1388 143.217 30.1388H143.009V31.7334ZM23.7308 31.9992C28.7267 31.9992 29.4293 27.7468 29.4293 19.9331C29.4293 12.0927 28.6747 7.86691 23.7308 7.86691C18.7869 7.86691 18.0323 12.0927 18.0323 19.9331C18.0323 27.7468 18.7348 31.9992 23.7308 31.9992ZM61.5387 31.7334H67.5495V30.1388H67.2632C66.925 30.1388 66.925 30.0059 66.925 29.5275V13.395C66.925 11.4549 66.925 10.7638 68.174 10.7638C69.0326 10.7638 69.0847 11.0828 69.0847 12.704V29.3946C69.0847 30.0059 68.8505 30.1122 68.6163 30.1122H68.3301V31.7068H74.6531V30.1122H74.3929C73.9505 30.1122 73.8464 29.9527 73.8464 29.3149V11.8269C73.8464 8.95658 73.1699 7.84033 70.4117 7.84033C68.6683 7.84033 67.6015 8.39846 66.951 10.1526V7.84033L61.1744 8.29215V9.88679H61.7989C62.1632 9.88679 62.4234 10.1526 62.4234 11.0562V29.2086C62.4234 29.9262 62.2673 30.1388 61.7989 30.1388H61.5647L61.5387 31.7334ZM116.937 31.9992C119.018 31.9992 119.773 30.9627 120.215 29.7401C120.605 31.574 121.048 31.9992 123.364 31.9992C126.096 31.9992 126.46 30.3248 126.408 27.0558H125.107C125.107 29.4212 125.055 29.6604 124.639 29.6604C124.248 29.6604 124.248 29.4743 124.248 28.1455V12.6508C124.248 9.51471 123.234 7.86691 119.643 7.86691C116.806 7.86691 113.97 8.74396 114.126 11.8801C114.204 13.3153 114.751 14.8302 116.234 14.7505C117.587 14.6707 118.212 13.9266 118.212 12.7572C118.212 11.9598 117.613 10.631 116.702 9.91337C117.041 9.7539 117.587 9.59444 118.498 9.59444C119.513 9.59444 119.695 10.3918 119.695 11.9067V16.2388C114.517 18.0461 113.424 18.7636 113.424 25.408C113.45 30.2185 114.595 31.9992 116.937 31.9992ZM107.985 31.9992C111.837 31.9992 112.747 30.8032 112.539 24.2652H110.952C110.952 29.4478 110.796 29.8464 109 29.8464C106.867 29.8464 106.867 28.8896 106.867 20.0128V19.2952H113.112C113.112 10.3386 112.149 7.86691 108.011 7.86691C104.265 7.86691 102.313 10.2323 102.313 20.0925C102.287 29.5009 103.77 32.0523 107.985 31.9992ZM56.1264 31.9992C59.9254 31.9992 60.9663 30.8032 60.8101 24.2652H59.2229C59.2229 29.4478 58.9887 29.8464 57.1412 29.8464C54.8254 29.8464 54.9555 28.8896 54.9555 20.0128V19.2952H60.9923C60.9923 10.3386 60.1856 7.86691 56.1004 7.86691C52.3274 7.86691 50.3759 10.2323 50.3759 20.0925C50.3759 29.5009 51.859 32.0523 56.1264 31.9992ZM127.267 31.7334H133.512V30.1388H133.043C132.653 30.1388 132.497 29.9261 132.497 29.2883V0L126.694 0.47839V2.07303H127.371C127.761 2.07303 127.917 2.2325 127.917 2.87036V29.368C127.917 29.9527 127.865 30.1388 127.605 30.1388H127.293L127.267 31.7334ZM81.6786 31.9992C84.5409 31.9992 86.0241 30.0856 86.0241 27.2153C86.0241 20.4912 80.0393 16.5843 80.0393 11.7472C80.0393 10.2589 80.5598 9.83364 81.5746 9.83364C83.0577 9.83364 83.2919 10.4715 83.5521 12.7306L83.7863 14.9631H85.5037V8.02637H84.1506C84.0725 8.90343 83.8904 9.22235 83.7863 9.11604C83.37 8.61107 82.3552 7.84033 80.6638 7.84033C78.1659 7.84033 75.9801 8.98316 75.9801 12.7837C75.9801 18.6839 81.9128 23.1755 81.9128 27.6936C81.9128 29.6338 81.4965 29.9527 80.1174 29.9527C78.7643 29.9527 78.4 29.3149 78.1398 26.3648L77.9317 24.3981H76.1363V31.7068H77.6975C77.8276 30.9095 78.0878 30.4045 78.322 30.6172C78.8424 31.255 79.3888 31.9992 81.6786 31.9992ZM134.683 26.79C134.683 30.564 135.229 31.9992 138.898 31.9992C141.474 31.9992 142.072 30.9627 142.072 28.0657V24.1057H140.511V28.2518C140.511 29.6338 140.381 29.7933 139.887 29.7933C139.366 29.7933 139.262 29.6338 139.262 28.4112V9.83364H142.098V8.02637H139.288V2.04646H137.701C136.556 7.36194 135.593 8.02637 134.058 8.02637H133.642V9.78048H134.683V26.79ZM23.7308 29.9527C22.6379 29.9527 22.6379 29.3149 22.6379 19.9065C22.6379 10.4715 22.6379 9.83364 23.7308 9.83364C24.8236 9.83364 24.8236 10.4715 24.8236 19.9065C24.8236 29.368 24.8236 29.9527 23.7308 29.9527ZM75.1995 9.03632C79.1026 6.08622 79.8832 3.85372 78.5822 1.80726C77.8796 0.691009 76.2664 -0.0265798 75.0954 0.956784C73.6903 2.15277 74.0286 4.22581 76.6567 5.42179C76.1883 5.97991 75.0954 7.06959 74.1847 7.97322L75.1995 9.03632ZM118.81 29.2086C117.873 29.2086 117.873 28.2252 117.873 25.0625C117.873 19.3218 117.873 18.551 119.747 17.5145V27.3481C119.747 28.8631 119.539 29.2086 118.81 29.2086ZM54.9555 17.6208C55.0336 10.1526 55.1637 9.80706 56.1785 9.80706C57.1412 9.80706 57.0892 10.2057 57.0892 17.6208H54.9555ZM106.867 17.6208C106.997 10.1526 107.049 9.80706 108.064 9.80706C109.078 9.80706 108.922 10.2057 108.922 17.6208H106.867Z"
                  fill="black"
                />
              </svg>
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “Like Uber for personal trainers”
            </p>
          </div>
        </div>
      </div>

      <div>
        <div className="tbk-flex tbk-flex-col tbk-justify-evenly lg:tbk-grid lg:tbk-grid-cols-3 lg:tbk-gap-8">
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://esquire.com/uk/life/fitness-wellbeing/g30150198/best-health-fitness-apps/"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <img
                className="tbk-inline-block"
                alt="Esquire"
                width="120px"
                height="32px"
                src="https://static.trubeapp.com/staging/assets/images/update/press/esquire-logo.png"
                srcSet="https://static.trubeapp.com/staging/assets/images/update/press/esquire-logo@2x.png 2x"
              />
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “Best for: last-minute PT sessions”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://forbes.com/sites/leebelltech/2019/01/02/best-health-and-fitness-apps-2019/?sh=78588bacca53"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <img
                className="tbk-inline-block"
                alt="Forbes"
                width="120px"
                height="32px"
                src="https://static.trubeapp.com/staging/assets/images/update/press/forbes-logo.png"
                srcSet="https://static.trubeapp.com/staging/assets/images/update/press/forbes-logo@2x.png 2x"
              />
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “Best Health And Fitness Apps 2019”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://harpersbazaar.com/uk/beauty/fitness-wellbeing/news/g38035/best-mum-baby-exercise-classes/"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <img
                className="tbk-inline-block"
                alt="Bazaar"
                width="129px"
                height="32px"
                src="https://static.trubeapp.com/staging/assets/images/update/press/bazaar-logo.png"
                srcSet="https://static.trubeapp.com/staging/assets/images/update/press/bazaar-logo@2x.png 2x"
              />
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “Postnatal PT with TruBe”
            </p>
          </div>
        </div>
      </div>

      <div>
        <div className="tbk-flex tbk-flex-col tbk-justify-evenly lg:tbk-grid lg:tbk-grid-cols-3 lg:tbk-gap-8">
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://standard.co.uk/esmagazine/how-to-live-like-a-millennial-millionaire-a3689661.html"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <img
                className="tbk-inline-block"
                alt="Evening Standard"
                width="246px"
                height="32px"
                src="https://static.trubeapp.com/staging/assets/images/update/press/evening-standard-logo.png"
                srcSet="https://static.trubeapp.com/staging/assets/images/update/press/evening-standard-logo@2x.png 2x"
              />
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “It’s the international jetsetter’s workout of choice”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0">
            <a
              href="https://cosmopolitan.com/uk/body/health/a35271/new-health-companies/"
              target="_blank"
              className="tbk-mb-2 tbk-inline-block"
              rel="noreferrer"
            >
              <svg
                width="167"
                height="32"
                viewBox="0 0 167 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="tbk-mb-2"
              >
                <path
                  d="M9.20161 1.11084C5.06716 1.11084 1.77515 5.05491 1.77515 16.0003C1.77515 25.4149 3.62187 30.8882 9.20161 30.8882C15.3036 30.8882 16.0258 24.8522 16.0258 19.8635L11.6102 19.6612C11.5285 23.8065 11.1284 26.4605 9.40186 26.4605C7.79639 26.4605 7.23532 25.4154 7.23532 16.0008C7.23532 6.26213 8.31685 5.53696 9.40186 5.53696C10.6859 5.53696 11.2876 7.30796 11.2876 12.0574L15.9438 11.896C15.9438 6.66507 15.2215 1.11084 9.20161 1.11084ZM24.0413 1.11136C19.4268 1.11136 16.4156 4.69321 16.4156 16.0008C16.4156 27.3068 19.4268 30.8887 24.0413 30.8887C28.6566 30.8887 31.6675 27.3066 31.6675 16.0008C31.6675 14.4366 31.6078 13.0253 31.4963 11.7458C33.2928 17.7547 40.5706 19.2054 40.5706 23.6854C40.5706 24.8523 39.9688 26.4606 38.242 26.4606C35.994 26.4606 35.994 23.6455 35.994 22.0355V22.0349H31.3378C31.3378 25.9799 32.4631 30.8882 38.0423 30.8882C41.1094 30.8882 43.234 29.3937 44.3391 27.1348V30.3253H48.1932V9.32049H48.2727L51.7651 30.3253H54.6546L58.1878 9.32049H58.2683V30.3253H62.9239V16.5499C63.0215 27.4188 66.0068 30.8887 70.546 30.8887C74.7043 30.8887 77.5591 27.9781 78.0841 19.1341V30.3253H83.3017V18.8572H85.3898C89.2071 18.8572 91.268 16.7541 91.9913 13.3865C91.9519 14.2095 91.9296 15.0769 91.9296 16.0008C91.9296 27.3068 94.9404 30.8887 99.5548 30.8887C104.172 30.8887 107.183 27.3066 107.183 16.0008C107.183 4.6932 104.172 1.11135 99.5548 1.11135C95.8751 1.11135 93.217 3.39414 92.2904 10.0509C92.2251 4.00149 89.1403 1.67579 83.3017 1.67579H78.0841V12.867C77.5591 4.02172 74.7043 1.11135 70.546 1.11135C66.0068 1.11135 63.0215 4.58135 62.9239 15.4517V1.67579H56.1399L53.6916 16.3619H53.6121L51.2831 1.67579H44.3391V8.01692C43.8538 3.86594 41.9594 1.11135 37.842 1.11135C33.4735 1.11135 31.149 4.53869 31.0999 8.78006C30.0229 3.09934 27.4775 1.11135 24.0413 1.11135V1.11136ZM107.294 1.6758V30.3253H119.176V25.8987H112.513V1.6758H107.294ZM120.012 1.6758V30.3253H125.23V1.6758H120.012ZM126.107 1.6758V6.10193H130.603V30.3253H135.821V6.10193H140.258L136.865 30.3253H141.24L141.962 23.9673H146.179L146.939 30.3253H151.674H151.958H156.089V12.9814H156.169L161.749 30.3253H165.802V1.6758H161.629V16.8043H161.549L156.773 1.6758H151.674V28.0241L148.424 1.6758H140.879L140.316 5.69175V1.6758H126.107ZM24.0413 5.53696C25.2463 5.53696 25.9678 6.78469 25.9678 12.9814V19.0181C25.9678 25.2146 25.2463 26.4605 24.0413 26.4605C22.8363 26.4605 22.1142 25.2146 22.1142 19.0181V12.9814C22.1142 6.78469 22.8363 5.53696 24.0413 5.53696ZM37.7198 5.53696C39.8073 5.53696 39.8073 8.23357 39.8073 9.84356H44.3391V18.9318C41.9854 13.9358 35.754 12.1031 35.754 8.15331C35.754 6.58385 36.4767 5.53696 37.7198 5.53696ZM70.546 5.53696C71.7492 5.53696 72.472 6.78469 72.472 12.9814V19.0181C72.472 25.2146 71.7492 26.4605 70.546 26.4605C69.3418 26.4605 68.6189 25.2146 68.6189 19.0181V12.9814C68.6189 6.78469 69.3418 5.53696 70.546 5.53696ZM99.5553 5.53696C100.761 5.53696 101.482 6.78469 101.482 12.9814V19.0181C101.482 25.2146 100.761 26.4605 99.5553 26.4605C98.3519 26.4605 97.6293 25.2146 97.6293 19.0181V12.9814C97.6293 6.78469 98.3519 5.53696 99.5553 5.53696ZM83.344 6.10191H83.743C85.4692 6.10191 87.0753 6.22211 87.0753 10.3671C87.0753 13.8272 85.9109 14.6732 83.7837 14.6732H83.344V6.10191ZM144.129 6.10191H144.21L145.734 19.7833H142.486L144.129 6.10191Z"
                  fill="#EC008C"
                />
              </svg>
            </a>
            <p className="tbk-text-main tbk-mb-1.5 tbk-grow tbk-text-grey-main">
              “5 new health companies we're totally obsessed with”
            </p>
          </div>
          <div className="tbk-mb-7 tbk-inline-flex tbk-w-full tbk-flex-col lg:tbk-mb-0" />
        </div>
      </div>
    </Slider>
  );
};

const AccordionItem = ({ className, title, text }) => {
  const [collapsed, setCollapsed] = useState(true);

  const handleClick = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  return (
    <div className="tbk-mb-2 tbk-border-b tbk-border-solid tbk-border-blue-grey-light">
      <button
        className={`${
          className ? `${className} ` : ""
        }tbk-flex tbk-w-full tbk-items-start tbk-justify-between tbk-pb-1 tbk-text-primary hover:tbk-text-coral`}
        onClick={handleClick}
      >
        <div className="tbk-text-h3-subtitle tbk-grow tbk-text-left">
          {title}
        </div>
        {collapsed ? <CaretDownIcon size={32} /> : <CaretUpIcon />}
      </button>
      <div
        className="tbk-text-main tbk-overflow-hidden tbk-text-left tbk-text-primary tbk-transition-height"
        style={{
          height: collapsed ? "0" : "auto",
          marginBottom: collapsed ? "0" : "24px",
        }}
      >
        {text}
      </div>
    </div>
  );
};

export default function AboutPage() {
  const { theme } = useTheme();
  const { user } = useAppState();

  const [showMoreQuestions, setShowMoreQuestions] = useState(false);

  const heroRef = React.useRef(null);
  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);
  const questionsSectionRef = useRef();

  const isUserLoggedIn = !!(user && user.id);

  const handleMoreQuestionsClick = useCallback(() => {
    setShowMoreQuestions(true);
  }, []);

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: About Us and our Mission</Title>
      <Meta
        name="description"
        content="TruBe: Top Provider of Personalised Fitness & Wellbeing Classes, offering expert trainers for bespoke Pilates, Yoga, HIIT, and more"
      />
      <Header
        behaviour="none"
        isFixed
        isLoggedIn={isUserLoggedIn}
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-min-h-[528px] tbk-pt-7 md:tbk-min-h-[auto]"
      >
        <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-7 tbk-left-0 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden">
          <picture className="tbk-h-full tbk-grow">
            <source
              media="(min-width: 768px)"
              srcSet={`${aboutHero} 1x, ${aboutHero2x} 2x, ${aboutHero2x} 3x`}
            />
            <source
              srcSet={`${aboutHeroMobile} 1x, ${aboutHeroMobile2x} 2x, ${aboutHeroMobile2x} 3x`}
            />

            <img
              className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
              src={aboutHero}
              alt="Best qualified and Insured Trainers"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-relative tbk-h-full tbk-pt-4 tbk-pb-8 md:tbk-grid md:tbk-grid-cols-become-member-layout md:tbk-gap-8 lg:tbk-pt-7 lg:tbk-pb-34">
            <div>
              <h1 className="tbk-text-epic-medium md:tbk-text-extra-bold tbk-mb-3 !tbk-leading-none tbk-text-basic-white lg:tbk-mb-32 lg:!tbk-leading-slight">
                Elevate Your
                <br />
                Well-Being
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-4 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-9">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-5 lg:tbk-mb-0">
              <h2 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4 lg:!tbk-leading-none">
                <span className="tbk-text-coral">Individual</span> Approach to
                Your Training
              </h2>
              <p className="tbk-text-main tbk-mb-3 tbk-text-primary lg:tbk-mb-7">
                At TruBe,  we empower individuals to achieve their fitness and
                wellness goals by connecting them with top-tier, certified
                coaches who provide personalized, convenient, and effective 1:1,
                in-person, training experiences. TruBe highlights the culture
                and sophistication from its London roots, where we bring forward
                a concierge style wellness approach to help each member optimize
                their goals through tailored personal sessions, cultivating
                powerful relationships with our coaches.
              </p>

              <div className="tbk-text-center lg:tbk-text-left">
                <Link to={isUserLoggedIn ? "/partners" : "/become-a-member"}>
                  <Button
                    size={"md"}
                    className="tbk-mt-3 tbk-mb-3 tbk-inline-flex tbk-pl-5 tbk-pr-5 lg:tbk-mt-0 lg:tbk-mb-0"
                    endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                  >
                    {isUserLoggedIn ? "Explore All Coaches" : "Become a Member"}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl lg:tbk-h-auto lg:tbk-bg-none">
              <video
                id="about-us-video"
                autoPlay
                loop
                className="tbk-relative tbk--z-1 tbk-h-full tbk-w-full tbk-object-cover"
                muted
                playsInline
                style={{
                  backgroundImage:
                    "url(https://assets-global.website-files.com/60785b43631c8625d8c10c7e/65d49af5c641d785ce83acc4_Webbg-1080-poster-00001.jpg)",
                }}
              >
                <source
                  src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/65d49af5c641d785ce83acc4_Webbg-1080-transcode.mp4"
                  type="video/mp4"
                />
                <source
                  src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/65d49af5c641d785ce83acc4_Webbg-1080-transcode.webm"
                  type="video/webm"
                />
              </video>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-relative tbk-bg-basic-white tbk-pt-4 lg:tbk-mb-0 lg:tbk-pt-9 ">
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-2">
          <div className="tbk-relative tbk-mb-4 tbk-text-center">
            <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              TruBe Success Stories
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
        </div>
      </div>

      <div className="tbk-bg-basic-white tbk-pb-24 lg:tbk-pb-24">
        <Slider
          className="home-stories-carousel tbk-overflow-hidden"
          dots
          dotsClass={"slick-dots tbk-left-1/2 tbk--translate-x-1/2 tbk-pl-3"}
          infinite={true}
          initialSlide={1}
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          nextArrow={<SliderNextArrowBig />}
          prevArrow={<SliderPrevArrowBig />}
          responsive={storiesSliderResponsiveSettings}
        >
          <div
            id="slide-1"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    I'm still a beginner to yoga, but even though I don't know
                    the names of all the poses and movements yet, I was still
                    able to follow along easily by the guidance of my
                    instructor. I worked up a sweat, but never felt overwhelmed.
                    My coach was really kind and Welcoming
                  </div>
                  <div>
                    <div className="tbk-text-main">Andrea W.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Yoga
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-2"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    This has been a phenomenal journey. My trainer has helped
                    push me to be in the best shape of my life. The focus on
                    results is phenomenal and so different from any other
                    trainer experience I've had before. Excited to keep pushing
                    and see even more results.
                  </div>
                  <div>
                    <div className="tbk-text-main">Brandon S.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Personal Training
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-3"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    No wonder I wasn't seeing results in the programs I've done
                    before this, I realized how little I knew! With everything
                    I'm learning with my coach, I'm confident that I'll see a
                    great transformation through the program and that I will be
                    able to carry these habits and learnings beyond the program,
                    which was my main motivator.
                  </div>
                  <div>
                    <div className="tbk-text-main">Jose H.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Personal Training
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-4"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    Thank you so much! At 29 I look better than I looked at 19!
                    And it's not only about slim body. I mean my self-confidence
                    too.
                  </div>
                  <div>
                    <div className="tbk-text-main">Mary K.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Pilates
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-5"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    I was surprised with how comfortable I was with talking
                    about the things going on in my head that could affect my
                    week/progress in the program. I’ve been supported through
                    all of it through the last three months and I can’t believe
                    how far I’ve come in my fitness journey! I am eternally
                    grateful for helping me get to where I am today (I can now
                    climb V1/V2 bouldering and 5.10a top-roping!) and I
                    definitely can see myself moving toward my goals with the
                    solid foundation she set up!
                  </div>
                  <div>
                    <div className="tbk-text-main">Jason G.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Personal Training
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-6"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    I've had a lot of trainers and you’ve been the best.
                    Attentive, knowledgeable, and patient, and pushing me to do
                    my best. You’ve helped me create a lifestyle, not just
                    something I'll give up on in a month.
                  </div>
                  <div>
                    <div className="tbk-text-main">James C.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      James C.
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div
            id="slide-7"
            className="slide tbk-h-116 tbk-px-1 tbk-py-3 lg:tbk-h-120 3xl:tbk-h-144"
          >
            <div className="content js-progressive-image slide-bg tbk-relative tbk-h-full tbk-w-full tbk-rounded-2xl tbk-bg-grey-light tbk-px-2 tbk-pt-2 tbk-pb-2 lg:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">
              <QuoteMark
                className="tbk-absolute"
                style={{
                  top: "-14px",
                  left: "14px",
                }}
              />
              <a>
                <div className="tbk-relative tbk-flex tbk-h-full tbk-flex-col tbk-justify-between tbk-rounded-lg">
                  <div className="tbk-text-secondary lg:tbk-text-main tbk-mb-1">
                    I feel so fortunate to have you as my personal trainer, yoga
                    teacher and nutrition coach. You have a wealth of experience
                    in fitness, exercise, health, and wellness and she is a
                    remarkably intuitive instructor and coach. I’m always being
                    challenged to achieve more, and you continuously vary our
                    routines to keep things interesting. The icing on the cake
                    is that you are a truly good person -- You’re friendly,
                    empathetic, and genuine. All in all, I give you my highest
                    recommendation.
                  </div>
                  <div>
                    <div className="tbk-text-main">Peter E.</div>
                    <div className="tbk-text-small tbk-text-blue-grey">
                      Yoga
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </Slider>
      </div>

      <section className="tbk-relative tbk-overflow-hidden tbk-bg-grey-light tbk-pb-0 md:tbk-pb-0 lg:tbk-max-h-[628px]">
        <div className="tbk-mx-auto tbk-flex tbk-flex-col tbk-justify-evenly md:tbk-grid md:tbk-grid-cols-2 xl:tbk-container">
          <div className="tbk-pb-7 tbk-pt-7 tbk-pl-4 tbk-pr-4 tbk-text-grey-main lg:tbk-pt-38 lg:tbk-pl-8 lg:tbk-pr-8 lg:tbk-pb-38">
            <div className="tbk-text-epic-bold lg:tbk-text-extra-bold tbk-mb-3 tbk-text-primary">
              Our <br className="tbk-hidden lg:tbk-block" />
              <span className="tbk-text-coral ">Mission</span>
            </div>
            <p className="tbk-text-h3-subtitle tbk-text-primary">
              Our mission is to provide personalized, in-person fitness and
              wellness coaching through a seamless platform. We offer convenient
              access to expert-level coaching, empowering you to prioritize
              mental and physical health, effortlessly guiding you towards your
              goals.
            </p>
          </div>

          <img
            className="tbk-top-0 tbk-right-0 tbk-h-full tbk-w-auto tbk-max-w-full tbk-object-cover xl:tbk-absolute"
            src={aboutBeBetter}
            srcSet={`${aboutBeBetter} 1x, ${aboutBeBetter2x} 2x`}
            alt="The best coaches and exercise sessions"
          />
        </div>
      </section>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-3 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-9">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <div className="tbk-relative tbk-mt-5 tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              How it works
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
          <div className="tbk-mb-7 md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-2 lg:tbk-grid-cols-4">
            <div className="tbk-mb-5 lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <img
                  className="tbk-w-full"
                  src={accessImg}
                  srcSet={`${access2xImg} 2x, ${access3xImg} 3x`}
                  alt="Coaches and Trainers near me"
                />
                <HowItWorks1 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                  Access
                </div>
              </div>
              <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-pb-1 tbk-text-center tbk-text-primary">
                Access and book the best trainers near you
              </div>
            </div>
            <div className="tbk-mb-5 lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <img
                  className="tbk-w-full"
                  src={connectImg}
                  srcSet={`${connect2xImg} 2x, ${connect3xImg} 3x`}
                  alt="Coaches and Trainers near me"
                />
                <HowItWorks2 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                  Connect
                </div>
              </div>
              <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-pb-1 tbk-text-center tbk-text-primary">
                Connect via text and develop the best session for you
              </div>
            </div>

            <div className="tbk-mb-5 lg:tbk-mb-0">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <img
                  className="tbk-w-full"
                  src={trainImg}
                  srcSet={`${train2xImg} 2x, ${train3xImg} 3x`}
                  alt="Coaches and Trainers near me"
                />
                <HowItWorks3 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                  Train
                </div>
              </div>
              <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-pb-1 tbk-text-center tbk-text-primary">
                Train 1 on 1 with continuous analysis & feedback
              </div>
            </div>

            <div className="">
              <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                <img
                  className="tbk-w-full"
                  src={progressImg}
                  srcSet={`${progress2xImg} 2x, ${progress3xImg} 3x`}
                  alt="Coaches and Trainers near me"
                />
                <HowItWorks4 className="tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                <div className="tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                  Progress
                </div>
              </div>
              <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-text-center tbk-text-primary">
                Progress with the same trainer and achieve your goals together
              </div>
            </div>
          </div>
          <Link
            className="tbk-block tbk-text-center"
            to={isUserLoggedIn || true ? "/partners" : "/become-a-member"}
          >
            <Button
              variant="secondary"
              className="tbk-inline-flex tbk-w-66 tbk-pl-5 tbk-pr-5"
              endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
            >
              Start now
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-flex !tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-about-tags-mobile tbk-bg-cover tbk-pb-3 tbk-pt-4 md:tbk-pt-7 md:tbk-pb-7 lg:tbk-bg-about-tags">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <p className="tbk-text-cta md:tbk-text-epic-medium tbk-mb-4 tbk-text-center tbk-uppercase tbk-text-basic-white md:tbk-mb-7 md:!tbk-leading-snug md:!tbk-tracking-tighter">
            <span className="">Conversation</span> fit passion body{" "}
            <span className="">spirit</span>{" "}
            <br className="tbk-hidden lg:tbk-block" />
            sensual determination <span className="">culture</span>{" "}
            <br className="tbk-hidden lg:tbk-block" />
            sensitive health rest motivation{" "}
            <br className="tbk-hidden lg:tbk-block" />
            food drink <span className="">love</span> live work play{" "}
            <br className="tbk-hidden lg:tbk-block" />
            <span className="">commerce</span> calm soothe stretch{" "}
            <br className="tbk-hidden lg:tbk-block" />
            breathe sweat organic exclusive{" "}
            <br className="tbk-hidden lg:tbk-block" />
            authentic premium aspire vision{" "}
            <br className="tbk-hidden lg:tbk-block" />
            dream <span className="">connection</span> humility grace{" "}
            <br className="tbk-hidden lg:tbk-block" />
            <span className="">community</span> privilege strength drive{" "}
            <br className="tbk-hidden lg:tbk-block" />
            pursuit ambition <span className="">mindful </span>
            <br className="tbk-hidden lg:tbk-block" />
            accountability unconditional{" "}
            <br className="tbk-hidden lg:tbk-block" />
            diversity <span className="">gratitude</span> inclusion
          </p>
          <div className="tbk-mb-1 tbk-text-center">
            <TruBeLogoVertical
              width={clientWidth() < theme.responsive.breakpoints.lg ? 74 : 94}
              height={clientWidth() < theme.responsive.breakpoints.lg ? 64 : 80}
              className="tbk-inline-block tbk-text-basic-white"
            />
          </div>
        </div>
      </div>

      <div className="tbk-flex !tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-0 lg:tbk-pt-8 lg:tbk-pb-0">
        <div className="tbk-container tbk-px-2 lg:tbk-px-2">
          <h2 className="tbk-text-epic-medium lg:tbk-text-extra-bold tbk-mb-5 !tbk-leading-none tbk-text-primary">
            Our <span className="tbk-text-coral">Mission</span>
          </h2>
          <p className="tbk-text-h3-subtitle lg:tbk-text-epic-medium tbk-mb-4 tbk-text-primary lg:tbk-mb-8">
            Our mission is to transform the well-being industry by providing
            immediate access to in-person, expert-level fitness and wellness
            coaching directly to you, whenever and wherever you want.
          </p>
          <p className="tbk-text-h3-subtitle lg:tbk-text-epic-medium tbk-mb-4 tbk-text-primary lg:tbk-mb-8">
            We believe in empowering you to prioritize your mental and physical
            well-being, providing convenient and tailored solutions that make
            achieving your fitness goals easy and enjoyable.
          </p>
          <p className="tbk-text-h3-subtitle lg:tbk-text-epic-medium tbk-mb-8 tbk-text-primary">
            Together, we'll embark on a journey of immersive, instructional, and
            motivational fitness and wellness programs. Welcome to the next
            generation of unlocking your TruBest self.
          </p>
          <hr className="tbk-border-blue-grey-light" />
        </div>
      </div>

      <div className="tbk-flex !tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-3 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-2">
          <h2 className="tbk-text-epic-medium lg:tbk-text-extra-bold tbk-mb-5 tbk-text-grey-main lg:tbk-mb-8">
            Our <span className="tbk-text-coral">Values</span>
          </h2>
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-gap-8">
            <div className="tbk-mb-7 md:tbk-mb-8">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-coral lg:tbk-mb-3 lg:!tbk-leading-none">
                Authenticity
              </h3>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-text-grey-main">
                Relationships with real people, in real time with trainers,
                coaches, mentors, and spiritual leaders.
              </p>
            </div>
            <div className="tbk-mb-7 md:tbk-mb-8">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-coral lg:tbk-mb-3 lg:!tbk-leading-none">
                Community Engagement
              </h3>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-text-grey-main">
                Shared brand equity that leverage, activate and celebrate brand
                collaborations.
              </p>
            </div>
            <div className="tbk-mb-7 md:tbk-mb-8">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-coral lg:tbk-mb-3 lg:!tbk-leading-none">
                Positive Social Impact
              </h3>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-text-grey-main">
                Today, in a post-pandemic era, people have the desire to feel
                good, look good, and do good.
              </p>
            </div>
          </div>
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-gap-8">
            <div className="tbk-mb-7 md:tbk-mb-8">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-coral lg:tbk-mb-3 lg:!tbk-leading-none">
                Innovative Creativity
              </h3>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-text-grey-main">
                A fresh look and feel in the physical and digital worlds.
              </p>
            </div>
            <div className="tbk-mb-8">
              <h3 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-coral lg:tbk-mb-3 lg:!tbk-leading-none">
                Personal <br />
                Growth
                <br />& Achievement
              </h3>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-text-grey-main">
                As humans, we are always growing and looking for ways to better
                ourselves.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex !tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-coral tbk-pt-7 tbk-pb-7 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8 lg:tbk-pb-0 xl:tbk-px-24">
          <h2 className="tbk-text-epic-medium lg:tbk-text-extra-bold tbk-text-grey-main lg:tbk-mb-2">
            Just the <span className="tbk-text-basic-white">Facts</span>
          </h2>
          <p className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-7 tbk-mb-7 tbk-text-grey-main lg:tbk-mb-7">
            Today’s Consumer Views Wellness
            <br className="tbk-hidden" /> Across Six Dimensions:
          </p>
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-gap-8">
            <div className="tbk-mb-5 lg:tbk-mb-0">
              <HeartbeatIcon />

              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Health
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Extends beyond medicine and supplements to include medical
                devices, telemedicine, and remote healthcare services, as well
                as personal health trackers
              </p>
            </div>

            <div className="tbk-mb-5 lg:tbk-mb-0">
              <SneakersIcon />
              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Fitness
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Was steadily increasing over time before experiencing some
                upheaval over the past year, with many consumers struggling to
                maintain pre-COVID-19 fitness levels
              </p>
            </div>

            <div className="tbk-mb-5 lg:tbk-mb-0">
              <HealthyFoodIcon />
              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Nutrition
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Has always been a part of wellness, but now consumers want food
                to help them accomplish their wellness goals in addition to
                tasting good
              </p>
            </div>

            <div className="tbk-mb-5 lg:tbk-mb-0">
              <SpaIcon />
              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Appearance
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Primarily relates to wellness-oriented apparel (“athleisure”)
                and beauty products (skincare and collagen supplements), but
                also includes service-oriented offerings like non surgical
                aesthetic procedures
              </p>
            </div>

            <div className="tbk-mb-5 lg:tbk-mb-0">
              <DoubleBedIcon />
              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Sleep
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Now goes beyond traditional sleep medication, like melatonin, to
                app-enabled sleep trackers and other sleep-enhancing products
              </p>
            </div>

            <div className="">
              <LotusIcon />
              <h3 className="tbk-text-title-bold tbk-mb-1 tbk-text-grey-main">
                Better Mindfulness
              </h3>
              <p className="tbk-text-main tbk-text-grey-main">
                Has gained mainstream consumer acceptance relatively recently,
                with meditation-focused apps and meditation-oriented offerings
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white">
        <div className="tbk-w-full">
          <div className="tbk-relative">
            <div className="tbk-grid tbk-grid-cols-2 tbk-gap-0.5">
              <div className="tbk-relative tbk-pt-[92.788462%] lg:tbk-pt-[64.469914%]">
                <img
                  className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
                  src={fitnessPillars}
                  srcSet={`${fitnessPillars} 1x, ${fitnessPillars2x} 2x,`}
                  alt="Fitness"
                />
                <div className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
                  Fitness
                </div>
              </div>

              <div className="tbk-relative tbk-pt-[92.788462%] lg:tbk-pt-[64.469914%]">
                <img
                  className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
                  src={passionPillars}
                  srcSet={`${passionPillars} 1x, ${passionPillars2x} 2x,`}
                  alt="Passion"
                />
                <div className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
                  Passion
                </div>
              </div>

              <div className="tbk-relative tbk-pt-[92.788462%] lg:tbk-pt-[64.469914%]">
                <img
                  className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
                  src={spiritPillars}
                  srcSet={`${spiritPillars} 1x, ${spiritPillars2x} 2x,`}
                  alt="Spirit"
                />
                <div className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
                  Spirit
                </div>
              </div>

              <div className="tbk-relative tbk-pt-[92.788462%] lg:tbk-pt-[64.469914%]">
                <img
                  className="tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-h-full tbk-w-full tbk-object-cover"
                  src={bodyPillars}
                  srcSet={`${bodyPillars} 1x, ${bodyPillars2x} 2x,`}
                  alt="Body"
                />
                <div className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-basic-white">
                  Body
                </div>
              </div>
            </div>
            <div className="lg:tbk-text-epic-bold tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-bg-pillars tbk-bg-contain tbk-bg-no-repeat tbk-px-2 tbk-pb-4 tbk-pt-4 tbk-text-grey-main lg:tbk-px-7 lg:tbk-pb-28 lg:tbk-pt-28">
              Pillars
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-overflow-hidden tbk-bg-basic-white tbk-pt-5 tbk-pb-24 lg:tbk-mb-0 lg:tbk-pt-9 lg:tbk-pb-0">
        <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-pb-1 lg:tbk-px-8 lg:tbk-pb-24">
          <div className="tbk-relative tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              TruBe in Press
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>

          {/*<div className="tbk-relative tbk-mt-1 tbk-mb-6 lg:tbk-mb-8 lg:tbk-mt-1">*/}
          {/*  <div className="tbk-bg-basic-white tbk-py-0 tbk-pr-2 lg:tbk-pl-2">*/}
          {/*    <div className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-inline-block tbk-text-grey-main">*/}
          {/*      Word on <span className="tbk-text-coral">the Street</span>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*  /!*<hr className="tbk-mx-auto tbk-mb-0 tbk--mt-[18px] tbk-border-blue-grey-light" />*!/*/}
          {/*</div>*/}

          <TruBeInPressSlider />
        </div>
      </div>

      <div
        id="faq"
        ref={questionsSectionRef}
        className="tbk-relative tbk-bg-basic-white tbk-pt-4 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-0"
      >
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-9">
          <div className="tbk-relative tbk-mb-7 tbk-text-center">
            <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Any Questions?
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>

          <div className="tbk-flex tbk-flex-col-reverse md:tbk-grid md:tbk-grid-cols-homepage-two-cols-layout md:tbk-items-start md:tbk-gap-8">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-bg-grey-light tbk-p-3">
              <img src={askConcierge} alt="Ask TruBe Concierge" />
              <p className="tbk-text-epic-medium tbk-my-4 tbk-text-center !tbk-leading-none tbk-text-primary">
                We're here
                <br /> to <span className="tbk-text-coral">help</span>
              </p>
              <Button
                size="md"
                className="tbk-w-68 lg:tbk-w-full"
                endIcon={<ArrowRightIcon />}
                onClick={() => setIsAskConciergeModalOpen(true)}
              >
                Ask TruBe Concierge
              </Button>
            </div>

            <div className="tbk-mb-5 tbk-text-center lg:tbk-mb-0">
              <AccordionItem
                title={"What is unique about TruBe Coaches?"}
                text={
                  "Your TruBe coach is a certified professional and expert in their field of fitness, sports, mindfulness and/or body/nutrition. All coaching on TruBe is done in-person at a location designated by the Member and at a time of their choice.  As a Member of TruBe you will be able to explore multiple fitness disciplines that best suit your workout goals. You can read about each TruBe coach (ratings, reviews, accreditation) and book your session."
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"How do I book a session with TruBe?"}
                text={
                  <p>
                    TruBe offers two booking platforms to make booking simple
                    and convenient:
                    <ul>
                      <li>
                        TruBe website{" "}
                        <a href="https://us.trubeapp.com">us.trubeapp.com</a>
                      </li>
                      <li>TruBe App available iOS.</li>
                      <li>
                        In order to book a session, you must first become a
                        member of TruBe. All memberships are FREE.
                      </li>
                    </ul>
                  </p>
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"What is the booking process with TruBe?"}
                text={
                  <ul>
                    <li>
                      Members can access the TruBe platform and explore all of
                      our offerings.
                    </li>
                    <li>
                      Select your Discipline of choice —> confirm your location
                      —> date & time of your preferred session —> book the coach
                      of your choice —-> purchase the session.{" "}
                      <b>
                        TruBe is here to make your fitness goals accessible and
                        efficient
                      </b>
                      .
                    </li>
                  </ul>
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"What is TruBe’s cancellation policy?"}
                text={
                  "Members can cancel any session with a 12 hour advanced notification. If you are outside of the cancellation forgiveness time, you will be responsible for the session cost. In certain extenuating circumstances, please reach out to our customer care team and we will work with you to rebook the session to the best of our ability."
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"How Long is a TruBe Session?"}
                text={"All TruBe sessions are one hour long."}
              />
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"What if I have an injury or special needs?"}
                  text={
                    <p>
                      If your medical professional has cleared you for exercise,
                      then working with a personal coach, in-person can be
                      highly beneficial for preventing or managing many common
                      chronic conditions and injuries. Your TruBe coach will
                      design safe, effective workouts that meet your specific
                      needs. If you have any further questions, please email us
                      at{" "}
                      <a href="mailto:members@trubeapp.com">
                        members@trubeapp.com
                      </a>
                      , and we'll be more than happy to make a recommendation.
                    </p>
                  }
                />
              ) : null}
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"How do I pay for a session on TruBe?"}
                  text={
                    "TruBe accepts all major forms of payment. As a member, you will have the ability to add in your payment details to your profile. You will only be charged for session when you book and complete your checkout."
                  }
                />
              ) : null}
              {/*{showMoreQuestions ? (*/}
              {/*  <AccordionItem*/}
              {/*    className="tbk-mt-3"*/}
              {/*    title={"How do I book repeat sessions?"}*/}
              {/*    text={*/}
              {/*      "TruBe offers you package deals to book multiple sessions at a given time. Visit the “packages” section in your profile for further information."*/}
              {/*    }*/}
              {/*  />*/}
              {/*) : null}*/}
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"Questions?"}
                  text={
                    <p>
                      Email us anytime at{" "}
                      <a href="mailto:members@trubeapp.com">
                        members@trubeapp.com
                      </a>
                      . We’re here to help!
                    </p>
                  }
                />
              ) : null}

              {!showMoreQuestions ? (
                <button
                  className="tbk-text-main tbk-mt-1 tbk-mb-1 tbk-inline-flex tbk-items-center tbk-text-blue-grey tbk-underline lg:tbk-mb-0"
                  onClick={handleMoreQuestionsClick}
                >
                  <div className="">More Questions</div>
                  <CaretDownIcon />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <AppBanner />

      <Footer />

      <AskConciergeModal
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />
    </>
  );
}
